//External Libraries
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faPaperPlaneTop as farPaperPlaneTop } from '@fortawesome/pro-regular-svg-icons/faPaperPlaneTop';
import { faXmark as farXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { faImage as farImage } from '@fortawesome/pro-regular-svg-icons/faImage';

//Hooks
import useCommentImagePicker from '../hooks/useCommentImagePicker';

//Components
import { CMButton } from '@chartmetric/chartmetric-design-system/components/CMButton';
import { CMFlex } from '@chartmetric/chartmetric-design-system/components/CMFlex';
import { CMText } from '@chartmetric/chartmetric-design-system/components/CMText';
import { CMInput } from '@chartmetric/chartmetric-design-system/components/CMInput';
import CommentImageThumbnail from './CommentImageThumbnail';
import CommentWarningToast from './CommentWarningToast';
import ImageLightbox from './ImageLightbox';

//Utils
import { COMMENT_IMAGE_ACCEPT, MAX_COMMENT_DISPLAY_NAME_LENGTH } from '../utils';

//Types
import type { AnnouncementCommentAttachment } from '../types';

interface CommentInputProps {
  inputId?: string;
  value: string;
  onChange: (val: string) => void;
  onSubmit: () => void;
  onCancel?: () => void;
  submitLabel?: string;
  onFocus?: () => void;
  isSubmitting: boolean;
  placeholder?: string;
  replyingToName?: string | null;
  onCancelReply?: () => void;
  canSetDisplayName?: boolean;
  displayName?: string;
  onDisplayNameChange?: (val: string) => void;
  existingAttachments?: AnnouncementCommentAttachment[];
  onRemoveExistingAttachment?: (id: number) => void;
  pendingImages?: File[];
  onImagesChange?: (files: File[]) => void;
}

function CommentInput({
  inputId = 'comment',
  value,
  onChange,
  onSubmit,
  onCancel,
  submitLabel,
  onFocus,
  isSubmitting,
  placeholder,
  replyingToName,
  onCancelReply,
  canSetDisplayName = false,
  displayName = '',
  onDisplayNameChange,
  existingAttachments = [],
  onRemoveExistingAttachment,
  pendingImages = [],
  onImagesChange,
}: CommentInputProps) {
  const { t } = useTranslation('common', { keyPrefix: 'announcementPanel' });
  const { fileInputRef, previews, handleFilePick, removeImage, openFilePicker, remainingSlots, warning, dismissWarning } =
    useCommentImagePicker({ pendingImages, onImagesChange, reservedCount: existingAttachments.length });
  const [enlargedImage, setEnlargedImage] = useState<{ src: string; alt: string } | null>(null);
  const handleCloseLightbox = useCallback(() => setEnlargedImage(null), []);

  return (
    <CMFlex vertical gap="xs" fullWidth>
      {replyingToName && (
        <CMFlex fullWidth justify="between" align="center" gap="xs">
          <CMText value={t('comments.replyingTo', { name: replyingToName })} variant="mini-1" color="activePrimary" />
          <CMButton iconLeft={farXmark} variant="secondaryText" size="sm" onClick={onCancelReply} />
        </CMFlex>
      )}
      {canSetDisplayName && (
        <CMInput
          id={`${inputId}-display-name`}
          type="text"
          label={t('comments.displayNameLabel')}
          value={displayName}
          onChange={(e) => onDisplayNameChange?.(e.target.value)}
          maxLength={MAX_COMMENT_DISPLAY_NAME_LENGTH}
          disabled={isSubmitting}
          size="sm"
        />
      )}
      {(existingAttachments.length > 0 || previews.length > 0) && (
        <CMFlex gap="xs" wrap>
          {existingAttachments.map((att) => (
            <CommentImageThumbnail
              key={att.id}
              url={att.image_url}
              alt={att.file_name}
              onClick={() => setEnlargedImage({ src: att.image_url, alt: att.file_name })}
              onRemove={() => onRemoveExistingAttachment?.(att.id)}
            />
          ))}
          {previews.map((p, i) => (
            <CommentImageThumbnail
              key={p.url}
              url={p.url}
              alt={p.name}
              onClick={() => setEnlargedImage({ src: p.url, alt: p.name })}
              onRemove={() => removeImage(i)}
            />
          ))}
        </CMFlex>
      )}
      <CMFlex align="stretch" gap="sm">
        <CMButton
          iconLeft={farImage}
          variant="secondary"
          size="sm"
          onClick={openFilePicker}
          disabled={isSubmitting || remainingSlots <= 0}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept={COMMENT_IMAGE_ACCEPT}
          multiple
          onChange={handleFilePick}
          className="hidden"
        />
        <CMInput
          id={`${inputId}-input`}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSubmit();
            if (e.key === 'Escape') onCancel?.();
          }}
          onFocus={onFocus}
          placeholder={placeholder}
          disabled={isSubmitting}
          size="sm"
        />
        <CMButton
          iconLeft={submitLabel ? undefined : farPaperPlaneTop}
          text={submitLabel}
          variant="primary"
          size="sm"
          onClick={onSubmit}
          disabled={isSubmitting || !value.trim()}
        />
        {onCancel && (
          <CMButton
            text={t('comments.cancel')}
            variant="secondaryText"
            size="sm"
            onClick={onCancel}
            disabled={isSubmitting}
          />
        )}
      </CMFlex>
      {enlargedImage && (
        <ImageLightbox src={enlargedImage.src} alt={enlargedImage.alt} onClose={handleCloseLightbox} />
      )}
      <CommentWarningToast message={warning} onDismiss={dismissWarning} />
    </CMFlex>
  );
}

export default CommentInput;
